import { useState } from "react";

import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import type { CandidateRow } from "@/infrastructure/supabase/database.types";

export interface CandidateFormValues {
  slug: string;
  name: string;
  narrative_md: string | null;
  commitments_md: string | null;
  pros_md: string | null;
  cons_md: string | null;
  time_to_mvp_weeks: number | null;
  tco_low_usd: number | null;
  tco_high_usd: number | null;
}

const DEFAULTS: CandidateFormValues = {
  slug: "",
  name: "",
  narrative_md: null,
  commitments_md: null,
  pros_md: null,
  cons_md: null,
  time_to_mvp_weeks: null,
  tco_low_usd: null,
  tco_high_usd: null,
};

interface CandidateFormBodyProps {
  initial: CandidateRow | null;
  onChange: (values: CandidateFormValues) => void;
  /** When false, hide narrative/commitments fields (use dedicated dialogs instead). */
  showLongForm?: boolean;
}

export function CandidateFormBody({
  initial,
  onChange,
  showLongForm = false,
}: CandidateFormBodyProps) {
  const [values, setValues] = useState<CandidateFormValues>(
    initial
      ? {
          slug: initial.slug,
          name: initial.name,
          narrative_md: initial.narrative_md,
          commitments_md: initial.commitments_md,
          pros_md: initial.pros_md,
          cons_md: initial.cons_md,
          time_to_mvp_weeks: initial.time_to_mvp_weeks,
          tco_low_usd: initial.tco_low_usd,
          tco_high_usd: initial.tco_high_usd,
        }
      : DEFAULTS
  );

  const update = (patch: Partial<CandidateFormValues>) => {
    const next = { ...values, ...patch };
    setValues(next);
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Slug">
          <Input
            value={values.slug}
            onChange={(e) =>
              update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
            }
            placeholder="loyalty-forward-hybrid"
            disabled={initial !== null}
          />
        </Field>
        <Field label="Name">
          <Input
            value={values.name}
            onChange={(e) => update({ name: e.target.value })}
            placeholder="Loyalty-Forward Hybrid"
          />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Time to MVP (weeks)">
          <Input
            type="number"
            value={values.time_to_mvp_weeks ?? ""}
            onChange={(e) =>
              update({ time_to_mvp_weeks: e.target.value === "" ? null : Number(e.target.value) })
            }
            placeholder="28"
          />
        </Field>
        <Field label="TCO low (ILS)">
          <Input
            type="number"
            value={values.tco_low_usd ?? ""}
            onChange={(e) =>
              update({ tco_low_usd: e.target.value === "" ? null : Number(e.target.value) })
            }
            placeholder="1200000"
          />
        </Field>
        <Field label="TCO high (ILS)">
          <Input
            type="number"
            value={values.tco_high_usd ?? ""}
            onChange={(e) =>
              update({ tco_high_usd: e.target.value === "" ? null : Number(e.target.value) })
            }
            placeholder="1800000"
          />
        </Field>
      </div>

      {showLongForm ? (
        <>
          <Field label="Narrative (markdown)">
            <Textarea
              rows={4}
              value={values.narrative_md ?? ""}
              onChange={(e) => update({ narrative_md: e.target.value || null })}
              placeholder="Why this bet…"
            />
          </Field>
          <Field label="Commitments (markdown)">
            <Textarea
              rows={4}
              value={values.commitments_md ?? ""}
              onChange={(e) => update({ commitments_md: e.target.value || null })}
              placeholder="Vendor contracts, hires required, IP & data ownership, exit cost…"
            />
          </Field>
        </>
      ) : null}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-foreground/80 text-[11.5px] font-medium">{label}</Label>
      {children}
    </div>
  );
}
